import React, { FunctionComponent, PropsWithChildren, useEffect } from 'react';
import { getNavbarCategories } from '../../services/configurator/navbar/NavbarConfigurator';
import { getArticlesByTagSlug } from '../../services/content/article/Article';
import { NavigationMenuContext } from '../../contexts/NavigationMenuContext';
import Layout from '../../components/Layout/Layout';
import Head from 'next/head';
import { SectionSecondaryContent } from '../../components/Section/SectionSecondaryContent';
import styles from '../../styles/Tags.module.scss';
import { ArticleModel } from '../../services/content/article/models/ArticleModel';
import { CategoryModel } from '../../services/content/category/models/CategoryModel';
import { getTagBySlug } from '../../services/content/tag/Tag';
import { TagModel } from '../../services/content/tag/models/TagModel';
import { setupTagsPageTargeting } from '../../services/ads/Targeting';
import { Pagination } from '../../components/Partials/Pagination/Pagination';
import { MetaDataModel } from '../../services/content/meta/models/MetaDataModel';
import { useRouter } from 'next/router';
import { AdBackground } from '../../components/Ads/AdBackground';
import { GoogleAdDesktop } from '../../components/Ads/GoogleAdDesktop';
import { ads } from '../../services/ads/constants';
import { GoogleAdMobile } from '../../components/Ads/GoogleAdMobile';
import { LdJson } from '../../components/LdJson/LdJson';
import { Pages } from '../../constants/Enums';
import { tagCache } from '../../services/Cache';

type Props = {
    articles: ArticleModel[];
    categories: CategoryModel[];
    tag: TagModel | null;
    meta: MetaDataModel;
    page: string;
    slug: string;
};

export async function getServerSideProps(context: any) {
    context.res.setHeader('Cache-Control', 'public, s-maxage=14, stale-while-revalidate=59');

    const slug = context.query.tag[0];
    const page = context.query.tag[1] ? context.query.tag[1] : '1';

    const cacheKey = `/tag?slug=${slug}&page=${page}`;

    if (tagCache.get(cacheKey)) {
        const props = tagCache.get(cacheKey);
        if (props) {
            return {
                props: {
                    articles: props.articles,
                    meta: props.meta,
                    categories: props.categories,
                    tag: props.tag,
                    page: page,
                    slug: slug,
                },
            };
        }
    }

    const categories = await getNavbarCategories();
    const articleListing = await getArticlesByTagSlug(slug, '18', page);
    const tag = await getTagBySlug(slug);
    // Check if tag is null or undefined
    if (tag === null) {
        return { notFound: true };
    }

    const articles = articleListing && articleListing.articles ? articleListing.articles : [];
    const meta = articleListing && articleListing.meta ? articleListing.meta : null;

    tagCache.set(cacheKey, {
        articles: articles,
        meta: meta,
        categories: categories,
        tag: tag,
        page: page,
        slug: slug,
    });

    return {
        props: {
            articles: articles,
            meta: meta,
            categories: categories,
            tag: tag,
            page: page,
            slug: slug,
        },
    };
}

const TagPage: FunctionComponent<Props> = ({ categories, articles, tag, meta, page, slug }) => {
    const router = useRouter();

    useEffect(() => {
        setupTagsPageTargeting(tag);
    }, []);

    return (
        <NavigationMenuContext.Provider value={categories}>
            <Layout>
                <Head>
                    <title>{tag?.title} - woman.bg</title>
                    <LdJson page={Pages.TAG} tag={tag} />
                    <meta name='description' content={tag?.description ?? ''} />

                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                         window['sprtlTargeting'] = {
                                section: ['tag'],
                                tag: [${tag?.id ?? ''}]
                        }`,
                        }}></script>
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('section', 'tag') : '';
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('tag', '${
                             tag ? tag.id : ''
                         }') : '';
                         `,
                        }}></script>
                    <link rel='canonical' href={`${process.env.NEXT_PUBLIC_WEBSITE_URL}/tag/${slug}`} />
                </Head>
                <main className='container'>
                    <div className='row'>
                        <div className='col-12 mb-2'>
                            <h1 className='font-playfair-700'>#{tag?.title}</h1>
                        </div>
                    </div>
                    <div className='row mb-5'>
                        <div className={`${styles.description} col-12 font-lato-200`}>{tag?.description}</div>
                    </div>
                    <div className='d-flex flex-row'>
                        <div className='container'>
                            <div className='row'>
                                {articles.map((article, idx) => {
                                    return (
                                        <AdsMobileWrapper
                                            idx={idx}
                                            key={`section-secondary-ads-mobile-key-${article.id}`}>
                                            <SectionSecondaryContent
                                                className={`${styles.secondaryContentContainer} col-md-4 col-lg-4 col-12 mb-md-3`}
                                                content={article}
                                                category={article?.category}
                                            />
                                        </AdsMobileWrapper>
                                    );
                                })}
                            </div>
                            <div className='w-100 text-center'>
                                <div className='d-inline-block d-md-none d-lg-none text-center'>
                                    <AdBackground>
                                        <GoogleAdMobile ad={ads[13]} />
                                    </AdBackground>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-12 d-flex flex-row justify-content-end'>
                                    <Pagination
                                        meta={meta}
                                        onPageClick={(page) => {
                                            router.push(page > 1 ? `/tag/${slug}/${page}` : `/tag/${slug}`);
                                        }}
                                        page={page}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='d-none d-md-block col-12 col-md-3 text-center'>
                            <AdBackground className='sticky-ad'>
                                <GoogleAdDesktop ad={ads[10]} />
                            </AdBackground>
                        </div>
                    </div>
                </main>
            </Layout>
        </NavigationMenuContext.Provider>
    );
};

export default TagPage;

const AdsMobileWrapper: FunctionComponent<PropsWithChildren<{ idx: number }>> = ({ children, idx }) => {
    const SECOND_ARTICLE = 1;
    const FOURTH_ARTICLE = 3;
    const SEVENTH_ARTICLE = 6;

    if (SECOND_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[10]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    if (FOURTH_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center ad-container-h600'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[11]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    if (SEVENTH_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[12]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    return <>{children}</>;
};
